import { FacetRegistry, FacetTypes, UNASSIGNED } from "../facets/Facets";
import { ElementFlags, QC } from "../schema/dt-schema";
import { DtConstants } from "../schema/DtConstants";
import { TandemCategories } from "../schema/TandemCategories";
import { MakeStringMatcher } from "./Search";
import { getAttributeDisplayUnit } from "../../measurement/UnitFormatter";
import { fitToView } from "../../tools/FitToViewUtil";
import { AttributeType } from "../schema/Attribute";
import { getLevelOfElement } from "../hud/layers/LayerUtils";
import { matchClassification } from "../schema/matchClassification";

const cls2filter = DtConstants.ClassificationAttribute2FilterKey;

function getFacetNodeIds(data) {
  const ids = new Set();

  function traverse(items) {
    for (let item of items) {
      ids.add(item.id);
      if (item.children?.length > 0) {
        traverse(item.children);
      }
    }
  }

  traverse(data);

  return ids;
}

async function getClassificationCodesWithParameters(facility) {
  const template = await facility.settings?.template?.get();
  if (!template) {
    return undefined;
  }

  const result = Object.values(cls2filter).reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});

  const parameters = template.psets[0].parameters;

  for (let param of parameters) {
    for (let key in param.applicationFilters) {
      for (let c of param.applicationFilters[key]) {
        result[key].push(c);
      }
    }
  }

  const hasMapping = Object.values(cls2filter).some((k) => result[k].length);

  return hasMapping ? result : undefined;
}

function isTaggedAsset(taggedClasses, cache, qc, code) {
  let isTagged = cache[cls2filter[qc]].get(code);
  if (isTagged !== undefined) {
    return isTagged;
  }

  isTagged = taggedClasses[cls2filter[qc]].some((c) => matchClassification(code, c));
  cache[cls2filter[qc]].set(code, isTagged);

  return isTagged;
};

function fitToSpaceFacets(facility) {
  const spaceFacets = facility.facetsManager.getFacet(FacetTypes.spaces);

  const spaces = {};
  for (let i = 0; i < spaceFacets.length; i++) {
    const facet = spaceFacets[i];
    if (!facet.selected || !facet.roomSrcModel) continue;

    const modelUrn = facet.roomSrcModel.urn();
    if (!spaces[modelUrn]) {
      spaces[modelUrn] = { model: facet.roomSrcModel, selection: [facet.dbId] };
    } else {
      spaces[modelUrn].selection.push(facet.dbId);
    }
  }

  // let the user briefly see the target in their currect "context" and then zoom to it
  setTimeout(() => fitToView(facility.viewer, Object.values(spaces)), 50);
}

function fitToIsolation(facility) {
  const isolation = facility.viewer.getAggregateIsolation();
  const target = isolation.map(({ model, ids }) => ({ model, selection: ids }));

  // let the user briefly see the target in their currect "context" and then zoom to it
  setTimeout(() => fitToView(facility.viewer, target), 50);
}

function _focusSpaceAndSelect(facility, model, dbId, level, rooms, options = {}) {
  if (!options.skipSelection) {
    facility.viewer.select(dbId, model);
  }

  const filterMap = facility.facetsManager.getFilterMap();
  facility.facetsManager.setFilterMap({
    ...filterMap,
    [FacetTypes.levels]: level ? [level.name] : [UNASSIGNED],
    [FacetTypes.spaces]: rooms.length ? rooms.map((room) => room.model.urn() + ':' + room.externalId) : [UNASSIGNED]
  }, options.merge, options.skipUpdate);

  if (!options.skipUpdate) {
    fitToSpaceFacets(facility);
  }
}

function _loadAndIsolate(facility, isolation) {
  // make sure the found elements are loaded before toggling their visibility
  for (let { model, ids } of isolation) {
    facility.facetsManager.setModelVisibility(model.urn(), true);
    model.loadElements(ids);
  }
  facility.viewer.impl.visibilityManager.aggregateIsolate(isolation);
  fitToIsolation(facility);
}

function addElementIsolationToResults(facility, isolation, results) {
  const count = isolation.reduce((c, i) => c + i.ids.length, 0);
  if (count > 0) {
    results.push({
      id: 'isolate-in-facility',
      label: `Isolate ${count} elements in facility`,
      data: {
        count
      },
      type: 'Elements',
      scope: 'Facility',
      run: () => {
        facility.facetsManager.setFilterMap({});
        _loadAndIsolate(facility, isolation);
      }
    });

    const viewIsolation = isolation.map(({ model, ids }) => ({
      model,
      ids: ids.filter((id) => model.visibilityManager?.isNodeVisible(id))
    })).filter(({ ids }) => ids.length > 0);

    const inViewCount = viewIsolation.reduce((c, i) => c + i.ids.length, 0);
    if (inViewCount > 0) {
      results.push({
        id: 'isolate-in-view',
        label: `Isolate ${inViewCount} elements in view`,
        data: {
          count: inViewCount
        },
        type: 'Elements',
        scope: 'View',
        run: () => {
          _loadAndIsolate(facility, viewIsolation);
        }
      });
    }
  }
}

function addAssetToResults(results, facility, model, id, name) {
  const rooms = facility.getRoomsOfElement(model, id);
  const level = getLevelOfElement(model, id);
  const location = rooms.length ? rooms.map((room) => room.name).join(', ') : level?.name;

  const pushToResults = (isViewScope) => {
    results.push({
      id: `asset-${isViewScope ? 'in-view-' : ''}${model.urn()}-${id}`,
      label: name,
      data: {
        id,
        modelUrn: model.urn(),
        location
      },
      type: 'Assets',
      scope: isViewScope ? 'View' : 'Facility',
      run: (options) => _focusSpaceAndSelect(facility, model, id, level, rooms, options)
    });
  };

  pushToResults(false);
  if (model.visibilityManager?.isNodeVisible(id)) {
    pushToResults(true);
  }
}

function addConnectedParameterToResults(results, params, isViewScope) {
  for (let id in params) {
    results.push({
      id: `connected-parameter-${isViewScope ? 'in-view-' : ''}${params[id].id}`,
      label: params[id].name,
      data: {
        id: params[id].id,
        uuid: params[id].uuid,
        streams: params[id].streams
      },
      type: 'ConnectedParameters',
      scope: isViewScope ? 'View' : 'Facility',
      run: () => {}
    });
  }
}

function addConnectionsToResults(facility, results, connections, isViewScope) {
  for (let stream of connections) {
    results.push({
      id: `connection-${isViewScope ? 'in-view-' : ''}${stream.dbId}`,
      label: stream.name,
      data: stream,
      type: 'Connections',
      scope: isViewScope ? 'View' : 'Facility',
      run: async (options) => {
        const defaultModel = facility.getDefaultModel();
        const rooms = facility.getRoomsOfElement(defaultModel, stream.dbId);
        const level = getLevelOfElement(defaultModel, stream.dbId);
        _focusSpaceAndSelect(facility, defaultModel, stream.dbId, level, rooms, options);
      }
    });
  }
}

function addSystemsToResults(facility, results, systems, isViewScope) {
  const defaultModel = facility.getDefaultModel();

  for (let system of systems) {
    results.push({
      id: `system-${isViewScope ? 'in-view-' : ''}${system.id}`,
      label: system.name,
      data: {
        id: system.id
      },
      type: 'Systems',
      scope: isViewScope ? 'View' : 'Facility',
      run: () => facility.viewer.select(system.dbId, defaultModel)
    });
  }
}

/**
 * 
 * @param {DtFacility} facility 
 * @param {string} term 
 * @param {{ logVerbose: boolean, deepSearch: boolean, displayUnit: string }} options 
 * @returns 
 */
export async function searchFacility(facility, term, options = {}) {
  const models = facility.facetsManager.models;

  const allFacetDefs = facility.facetsManager.getFacetDefs();
  const facetDefs = allFacetDefs.slice(1); // strip model facet;
  const hasLevelFacet = !!facetDefs.find((def) => def.id === FacetTypes.levels);
  const taggedClasses = await getClassificationCodesWithParameters(facility);

  const results = [];
  options.logVerbose && console.time("Searching");

  const matcher = MakeStringMatcher(term);
  const getMatch = (text) => matcher(typeof text === "string" ? text : String(text));

  const checkedClasses = {
    [cls2filter[QC.TandemCategory]]: new Map(),
    [cls2filter[QC.Classification]]: new Map()
  };
  const streamsIdSet = new Set();
  const systemsIdSet = new Set();

  // search facets and elements
  {
    const isolation = [];
    const filterMap = facility.facetsManager.getFilterMap();

    const facets = facility.facetsManager.getFacets();
    const facetsInView = allFacetDefs.reduce((inView, def, i) => {
      inView[def.id] = getFacetNodeIds(facets[i]);
      return inView;
    }, {});

    const allModels = {
      [FacetTypes.models]: models.map((m) => m.urn())
    };

    const mergedFacets = facility.facetsManager.collectUnfilteredMergedFacets().slice(1); // skipping models facet
    const matchedMergedFacets = [];

    const traverseFacets = (def, inViewBaseFilter, facets, matchedFacets) => {
      for (const facet of facets) {
        const id = facet.id; // note, facetId is always a string, while this might be e.g. numeric
        let label = facet.label || facet.id;

        const labelMatch = getMatch(label);
        const facetDescriptionMatch = getMatch(facet.description);
        if (labelMatch.match || facet.description && facetDescriptionMatch.match) {

          matchedFacets.push(facet);

          // take the greater of the two scores if both match, otherwise use the relevant one
          let matchScore;
          if (labelMatch.match && facet.description && facetDescriptionMatch.match) {
            matchScore = Math.max(labelMatch.score, facetDescriptionMatch.score);
          } else {
            matchScore = labelMatch.match ? labelMatch.score : facetDescriptionMatch.score;
          }

          const resultId = `${def.id}_${id}`;
          // include the facet's description in the label
          if (facet.description) {
            label += ` (${facet.description})`;
          }

          const filter = {
            [def.id]: [id]
          };
          // if the matching facet is a room, also add the corresponding level facet to the filter
          if (hasLevelFacet && def.id === FacetTypes.spaces) {
            let modelData = facet.roomSrcModel?.getData();
            if (modelData) {
              const { dbId2levelId, levelMap } = modelData;
              const level = levelMap[dbId2levelId[facet.dbId]];
              if (level) {
                filter[FacetTypes.levels] = [level.name];
              }
            }
          }
          // test if facet is in view
          if (facetsInView[def.id]?.has(id)) {
            results.push({
              id: 'facet-in-view-' + resultId,
              label: typeof label === "string" ? label : String(label),
              data: {
                id,
                facetDefId: def.id,
                facetDefLabel: def.label
              },
              type: `Facets`,
              scope: 'View',
              run: (options) => {
                facility.facetsManager.setFilterMap({
                  ...inViewBaseFilter,
                  ...filter
                }, options?.merge, options?.skipUpdate);
                !options?.skipUpdate && fitToIsolation(facility);
              },
              matchScore
            });
          }

          results.push({
            id: 'facet-' + resultId,
            label: typeof label === "string" ? label : String(label),
            data: {
              id,
              facetDefId: def.id,
              facetDefLabel: def.label
            },
            type: `Facets`,
            scope: 'Facility',
            run: (options) => {
              facility.facetsManager.setFilterMap({
                ...allModels,
                ...filter
              }, options?.merge, options?.skipUpdate);
              !options?.skipUpdate && fitToIsolation(facility);
            },
            matchScore
          });
        }

        if (facet.children) {
          traverseFacets(def, inViewBaseFilter, facet.children, matchedFacets);
        }
      }
    };
    const inViewBaseFilter = { [FacetTypes.models]: filterMap[FacetTypes.models] };
    for (let i = 0; i < mergedFacets.length; i++) {

      const facetDef = facetDefs[i];
      inViewBaseFilter[facetDef.id] = filterMap[facetDef.id];
      const matchedFacets = [];

      traverseFacets(facetDef, inViewBaseFilter, mergedFacets[i], matchedFacets);

      matchedMergedFacets.push(matchedFacets);
    }


    await Promise.all(models.map((model) => {
      const searchModel = async () => {
        const { dbId2flags, dbId2ftypeId, dbId2TandemCategory, dbId2Class } = model.getData();
        const idSet = new Set();
        const it = model.getInstanceTree();

        const addToIdSet = (id) => {
          // Split elements into streams, systems, and element results
          if (dbId2flags[id] === ElementFlags.Stream) {
            streamsIdSet.add(id);
          } else if (dbId2flags[id] === ElementFlags.System) {
            systemsIdSet.add(id);
          } else {
            idSet.add(id);
          }
        };

        for (let facets of matchedMergedFacets) {
          for (let facet of facets) {
            const ids = facet.ids.get(model);
            if (ids) {
              for (let id of ids) {
                addToIdSet(id);
              }
            }
          }
        }

        // loop over node names too
        const getName = (id) => it.getNodeName(id);

        for (let dbId = 0; dbId < model.getElementCount(); ++dbId) {

          const flags = dbId2flags[dbId];
          if ((flags & ElementFlags.AllLogicalMask) !== 0 && flags !== ElementFlags.Level && flags !== ElementFlags.Stream && flags !== ElementFlags.System && flags !== ElementFlags.GenericAsset) {
            continue;
          }

          const { match } = getMatch(getName(dbId));
          if (match) {
            addToIdSet(dbId);
          }
        }

        // do deep search (optionally)
        if (options.deepSearch) {
          const ftypeIds = new Set();
          const ids = await model.search({ text: term, displayUnit: options.displayUnit });
          ids.forEach((id) => {
            const flags = dbId2flags[id];
            if (flags === ElementFlags.FamilyType) {
              // Element matching search is a "type" element which does not have geometry.
              // Isolation result should consist of elements with geometry.
              // Collect this parent element so we can add the children:
              ftypeIds.add(id);
            } else {
              addToIdSet(id);
            }
          });

          if (ftypeIds.size) {
            // Add child elements of collected parent "type" elements:
            for (let childDbId = 0; childDbId < dbId2ftypeId.length; ++childDbId) {
              const parentDbId = dbId2ftypeId[childDbId];
              if (ftypeIds.has(parentDbId)) {
                addToIdSet(childDbId);
              }
            }
          }
        }

        if (idSet.size) {
          const ids = Array.from(idSet);
          isolation.push({ model, ids });

          if (taggedClasses) {
            for (let dbId of ids) {
              if (model.isLevel(dbId) || model.isRoom(dbId)) {
                continue;
              }

              // Collect assets
              let isTagged = false;
              const tcCode = TandemCategories.index2code(dbId2TandemCategory[dbId]);
              if (tcCode) {
                isTagged = isTaggedAsset(taggedClasses, checkedClasses, QC.TandemCategory, tcCode);
              }
              if (!isTagged) {
                const clsCode = dbId2Class.getById(dbId);
                if (clsCode) {
                  isTagged = isTaggedAsset(taggedClasses, checkedClasses, QC.Classification, clsCode);
                }
              }

              if (isTagged) {
                addAssetToResults(results, facility, model, dbId, it.getNodeName(dbId));
              }
            }
          }
        }
      };
      return searchModel();
    }));

    if (isolation.length) {
      addElementIsolationToResults(facility, isolation, results);
    }
  }

  // search connections & connected parameters
  const defaultModel = facility.getDefaultModel();
  if (defaultModel) {
    const [streams, params] = await Promise.all([
    facility.streamMgr.getAllStreamInfos(),
    facility.streamMgr.getAllConnectedAttributes()]
    );

    const paramMatches = {};
    const streamMatches = [];

    for (let param of params) {
      if (getMatch(param.name).match) {
        paramMatches[param.id] = { ...param, streams: [] };
      }
    }

    for (let stream of streams) {
      const streamMatch = streamsIdSet.has(stream.dbId);
      const paramMatch = stream.streamAttrs.filter((a) => paramMatches[a.id]).map((a) => a.id);

      if (streamMatch || paramMatch.length) {
        streamMatches.push({ ...stream, parameterMatches: paramMatch });
      }
      stream.streamAttrs.forEach((attr) => paramMatches[attr.id]?.streams.push(stream.dbId));
    }

    addConnectedParameterToResults(results, paramMatches, false);
    addConnectionsToResults(facility, results, streamMatches, false);

    if (defaultModel.visibilityManager) {
      const streamMatchesInView = streamMatches.filter((s) => defaultModel.visibilityManager?.isNodeVisible(s.dbId));

      if (streamMatchesInView.length) {
        addConnectionsToResults(facility, results, streamMatchesInView, true);
        // Filter out parameters that have no visible streams in the current view
        const paramMatchesInView = {};
        for (let id in paramMatches) {
          const streams = paramMatches[id].streams.filter((dbId) => defaultModel.visibilityManager.isNodeVisible(dbId));
          if (streams.length) {
            paramMatchesInView[id] = { ...paramMatches[id], streams };
          }
        }

        addConnectedParameterToResults(results, paramMatchesInView, true);
      }
    }
  }

  // Search Systems
  if (defaultModel) {
    const systems = await facility.systemsManager.getAll();
    const sysMatches = systems.filter((sys) => systemsIdSet.has(sys.dbId));

    addSystemsToResults(facility, results, sysMatches, false);

    // System is in view when at least one element from it is visible
    let sysClasses = 0;
    models.forEach((model) => {
      const visibleDbIds = model.getVisibleDbIds();
      const { dbId2SystemClass, systemClasses } = model.myData;
      for (let dbId of visibleDbIds) {
        sysClasses |= dbId2SystemClass[dbId];

        if ((sysClasses & systemClasses) === systemClasses) {
          // All systems classes in the model are visible
          break;
        }
      }
    });

    const systemMatchesInView = sysMatches.filter((sys) => sysClasses & sys.filter.systemClasses);

    addSystemsToResults(facility, results, systemMatchesInView, true);
  }

  // search views
  {
    const views = await facility.getSavedViewsList();
    for (let view of views) {
      const viewNameMatch = getMatch(view.viewName);
      if (viewNameMatch.match) {
        results.push({
          id: "view-" + view.id,
          label: view.viewName,
          data: {
            view
          },
          type: 'Views',
          run: () => facility.goToView(view),
          matchScore: viewNameMatch.score
        });
      }


      const dashboardNameMatch = getMatch(view.dashboardName);
      if (view.dashboardName && dashboardNameMatch.match) {
        results.push({
          id: "dashboard-" + view.id,
          label: view.dashboardName,
          data: {
            view
          },
          type: 'Dashboards',
          matchScore: dashboardNameMatch.score
        });
      }
    }
  }

  // search documents
  {
    facility.settings?.docs?.forEach((doc) => {
      const docNameMatch = getMatch(doc.name);
      if (docNameMatch.match) {
        results.push({
          id: "doc-" + doc.id,
          label: doc.name,
          data: {
            doc
          },
          type: 'Docs',
          matchScore: docNameMatch.score
        });
      }
    });
  }

  // search standard facets
  {
    for (const facetId in FacetRegistry) {
      if (facetId === FacetTypes.attributes) {
        continue;
      }

      const facet = new FacetRegistry[facetId]();
      const labelMatch = getMatch(facet.label);
      if (labelMatch.match) {
        results.push({
          id: "standard-attr-" + facetId,
          label: facet.label,
          data: {
            facetId
          },
          type: "StandardAttribute",
          run: async () => {
            if (facility.facetsManager.getFacetDef(facetId)) return;
            facility.facetsManager.addFacetDef(facet.getSettings());
          },
          matchScore: labelMatch.score
        });
      }
    }
  }

  // search attrs
  {
    const seenHashes = new Set();
    for (let model of models) {
      const attrs = await model.getAttributes({ source: true, native: true });
      for (let attr of attrs) {
        if (seenHashes.has(attr.hash)) {
          continue;
        }
        seenHashes.add(attr.hash);
        const attrNameMatch = getMatch(attr.name);
        // Check for allowed values (tags)
        let allowedValuesMatch;
        if (attr.dataType === AttributeType.StringList || attr.dataType === AttributeType.String && attr.allowedValues?.list) {
          allowedValuesMatch = attr.allowedValues.list.find((tag) => getMatch(tag).match);
        }

        if (attrNameMatch.match || allowedValuesMatch) {
          let attrLabel = attr.name;

          if (attr?.forgeUnit) {
            const displayUnit = getAttributeDisplayUnit(attr);

            if (displayUnit) {
              attrLabel += ` (${displayUnit})`;
            }
          }
          attrLabel += attr.category ? ` (${attr.category})` : ' Standard';

          results.push({
            id: "attr-" + attr.hash,
            label: attrLabel,
            data: {
              attr,
              allowedValuesMatch
            },
            type: 'Attribute',
            run: async () => {
              // TODO: it's way too difficult to add a facet [TS: I agree]
              const fm = facility.facetsManager;
              const id = FacetTypes.attributes + "_" + attr.hash;
              if (!fm.getFacetDef(id)) {
                const defs = await fm.addFacetDef({
                  attributeHash: attr.hash,
                  id
                });
                const settings = defs.map((d) => d.getSettings());
                await fm.setSettings(settings, fm.hiddenCategories);
              }
              if (allowedValuesMatch) {
                fm.setFilterMap({ ...fm.getFilterMap(), [id]: [allowedValuesMatch] });
              }
            },
            matchScore: attrNameMatch.score
          });
        }
      }
    }
  }

  results.sort((a, b) => {
    if (a.matchScore === b.matchScore) {
      return a.label.localeCompare(b.label);
    }
    return (b.matchScore || 0) - (a.matchScore || 0);
  });

  options.logVerbose && console.timeEnd("Searching");
  return {
    results,
    matcher
  };
}